import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaCheck, FaDownload, FaTimes, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as general from '../../libs/general';
import { API_URL_All } from '../../api';
import { CheckingTicket } from '../tickets/Tickets';
import axiosInstance from '../../axiosInstance';
import ReconciliationPaymentConfirmModal from '../reconciliation/ReconciliationPaymentConfirmModal';

/**
 * Modal xem trước (tạo mới, data.listIds) / xem chi tiết (data.id) 1 kỳ đối soát TTH — mirror
 * NormalReconciliationReview.jsx (layout fullscreen trượt từ trên xuống, không phải centered) nhưng
 * field theo TTH (số lượng Tệ, tỷ giá, chênh lệch thay vì cân/m3/mã vận đơn).
 */
const TthReconciliationReview = ({ data, handleSearch, canEdit = false, canDelete = true, nestedModal = false, onClose }) => {
  const [show, setShow] = useState(true);
  const [model, setModel] = useState({});
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);

  useEffect(() => {
    retrieveModalData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const retrieveModalData = async (params) => {
    if (params.id) {
      try {
        const res = await axiosInstance.get('/api/tth-invoices/getTthInvoice', { params: { id: params.id } });
        if (res.data?.success === false) { toast.error(res.data.message); return; }
        setModel(res.data.data);
        setShow(true);
      } catch (err) {
        toast.error(err.message);
      }
      return;
    }
    setModel({});
    try {
      const res = await axiosInstance.post('/api/tth-invoices/getTthInvoiceReview', { codes: params.listIds.join(';') },
        { headers: { 'Content-Type': 'application/json' } });
      if (res.data?.success === false) { toast.error(res.data.message); return; }
      setModel(res.data.data);
      setShow(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleModalClose = () => {
    setShow(false);
    if (typeof onClose === 'function') onClose();
    else if (handleSearch) handleSearch();
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const res = await axiosInstance.post('/api/tth-invoices/saveTthInvoice', model);
      if (!res.data?.success) {
        toast.error(res.data?.message || 'Lưu thất bại');
        return;
      }
      toast.success(res.data.message || 'Tạo kỳ đối soát thành công');
      if (handleSearch) handleSearch();
      await retrieveModalData({ id: res.data.data });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleDeleteInvoice = () => {
    if (!canDelete) return;
    general.openConfirm(async () => {
      setLoadingDelete(true);
      try {
        const res = await axiosInstance.get('/api/tth-invoices/deleteTthInvoice', { params: { id: model.id } });
        if (!res.data?.success) { toast.error(res.data.message); return; }
        toast.success(res.data.message);
        if (handleSearch) handleSearch();
        handleModalClose();
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoadingDelete(false);
      }
    });
  };

  const handlePaymentSuccess = () => {
    if (handleSearch) handleSearch();
    handleModalClose();
  };

  const handleExportInvoice = async (param) => {
    setLoadingExport(true);
    try {
      var requestApi = `${API_URL_All}/api/tth-invoices/exportTthInvoice?id=${param.id}`;
      var fileName = `Doi_soat_tth_${param.id}.xlsx`;
      if (!param.id) {
        fileName = `Doi_soat_tth_preview.xlsx`;
        const codes = data.listIds.join(';');
        requestApi = `${API_URL_All}/api/tth-invoices/exportTthInvoice?codes=${codes}`;
      }
      await general.downloadFile(requestApi, fileName);
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleModalClose}
        size="fullscreen"
        dialogClassName="modal-popupfull checking-review-dialog"
        className={[
          "checking-review-modal",
          nestedModal ? "accounting-nested-modal" : "",
        ].filter(Boolean).join(" ")}
        backdropClassName={nestedModal ? "accounting-nested-modal-backdrop" : undefined}
        enforceFocus={!nestedModal}
        backdrop="static"
      >
        <div className="modal-dialog11 modal-fullscreen11">
          <form className="normal-form" onSubmit={handleSubmitForm}>
            <div className="modal-content">
              <div className="modal-header position-relative flex-between">
                {model.id && (<span className="record_id">ID: {model.id}</span>)}
                <h5 className="modal-title">{model.id ? `Chi tiết phiếu đối soát TTH #${model.id}` : 'Xem trước phiếu đối soát TTH'}</h5>
                <button type="button" className="btn-close" onClick={handleModalClose} aria-label="Đóng"></button>
              </div>
              <div className="modal-body">
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label className="form-label col-form-label me-2">Đối tác:</label>
                    <b>{model.partnerCode}{model.partnerName ? ` - ${model.partnerName}` : ''}</b>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label col-form-label me-2">Trạng thái:</label>
                    <CheckingTicket checkingId={model.checkingId} checkingName={model.checkingName} />
                    <span className="ms-1">{model.checkingName}</span>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-3">Số đơn: <b>{model.orderCount}</b></div>
                  <div className="col-md-3">Tổng Tệ: <b>{general.formatDecimal(model.totalQuantity)} CNY</b></div>
                  <div className="col-md-3">Tổng tiền theo KH: <b>{general.formatNumber(model.totalAmountCustomer)} VND</b></div>
                  <div className="col-md-3">Tổng chênh lệch: <b className="text-primary">{general.formatNumber(model.totalAmountDiff)} VND</b></div>
                </div>
                {!model.id && (
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label className="form-label">Ghi chú</label>
                      <input className="form-control" value={model.note || ''}
                        onChange={(e) => setModel({ ...model, note: e.target.value })} />
                    </div>
                  </div>
                )}

                <section className="p-1">
                  <div className="table-responsive border table-normal table-sticky">
                    <table className="table table-striped table-bordered">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center" width="40px">STT</th>
                          <th className="text-center">Mã đơn</th>
                          <th className="text-center">Ngày</th>
                          <th className="text-center">Mã KH</th>
                          <th className="text-center">Số lượng Tệ</th>
                          <th className="text-center">Tỷ giá KH</th>
                          <th className="text-center">Tỷ giá ĐT</th>
                          <th className="text-center">Tiền KH</th>
                          <th className="text-center">Giá trị ĐT</th>
                          <th className="text-center">Chênh lệch</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(model.items || []).map((item, index) => (
                          <tr key={index}>
                            <td className="text-center">{index + 1}</td>
                            <td className="text-center">{item.orderCode}</td>
                            <td className="text-center" title={general.formatTimeShort(item.orderDate)}>{general.formatDate(item.orderDate)}</td>
                            <td className="text-center">{item.customerCode}</td>
                            <td className="text-center">{general.formatDecimal(item.quantity)}</td>
                            <td className="text-center">{general.formatNumber(item.customerRate)}</td>
                            <td className="text-center">{general.formatNumber(item.partnerRate)}</td>
                            <td className="text-center">{general.formatNumber(item.amountCustomer)}</td>
                            <td className="text-center">{general.formatNumber(item.amountPartner)}</td>
                            <td className="text-center">{general.formatNumber(item.amountDiff)}</td>
                          </tr>
                        ))}
                        {(!model.items || model.items.length === 0) && (
                          <tr><td colSpan="10" className="text-center">Không có dữ liệu</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
              <div className="modal-footer flex-between w-100">
                <div className="d-flex align-items-center">
                  <span
                    className={`btn-sm btn btn-primary me-2 d-inline-flex align-items-center gap-1${loadingExport ? ' disabled' : ''}`}
                    onClick={loadingExport ? undefined : () => handleExportInvoice({ id: model.id })}
                  >
                    {loadingExport ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Đang xuất...
                      </>
                    ) : (
                      <>
                        <FaDownload size={12} /> {model.id ? 'Xuất excel' : 'Xuất excel nháp'}
                      </>
                    )}
                  </span>
                  {model.id && canDelete && model.checkingId !== 15 && (
                    <button type="button" className="btn-small d-inline-flex align-items-center gap-1" disabled={loadingDelete} onClick={handleDeleteInvoice}>
                      {loadingDelete ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          Đang xóa...
                        </>
                      ) : (
                        <>
                          <FaTrash size={12} /> Xóa
                        </>
                      )}
                    </button>
                  )}
                </div>
                <div>
                  {!model.id && (
                    <button type="submit" className="btn-sm btn btn-primary d-inline-flex align-items-center gap-1" disabled={loadingSubmit}>
                      {loadingSubmit ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          Đang xử lý...
                        </>
                      ) : (
                        <>
                          <FaCheck size={12} /> Tạo đối soát
                        </>
                      )}
                    </button>
                  )}
                  {model.id && model.checkingId === 14 && canEdit && (
                    <span
                      className="btn-sm btn btn-primary me-2 d-inline-flex align-items-center gap-1"
                      onClick={() => setShowPaymentConfirm(true)}
                    >
                      <FaCheck size={12} /> Xác nhận đối soát
                    </span>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      <ReconciliationPaymentConfirmModal
        show={showPaymentConfirm}
        onHide={() => setShowPaymentConfirm(false)}
        onSuccess={handlePaymentSuccess}
        completeApi="/api/tth-invoices/completeTthInvoice"
        payloadBase={{ id: model.id }}
        summary={{
          id: model.id,
          partyCode: model.partnerCode,
          partyName: model.partnerName,
          amount: model.totalAmountDiff,
          orderCount: model.orderCount,
          dateText: model.createdAt ? general.formatDate(model.createdAt) : '',
        }}
        accountLabel="Tài khoản nhận tiền chênh lệch"
      />
    </>
  );
};

export default TthReconciliationReview;
